/* eslint-disable no-console */
import { FC } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import Countdown, { CountdownRenderProps } from 'react-countdown';
import { useSelector } from 'react-redux';
import { Box, Text, Tezos, Tooltip } from '@components/design-system';
import { RootState } from '@state/store';
import { getFiat } from '@utils/format';
import * as Styles from './styles';

export interface IBiddingCountDown {
  currentBid: number;
  auctionEnd: number;
  isMinBid: boolean;
  simple?: boolean;
}

export const BiddingCountDown: FC<IBiddingCountDown> = ({ currentBid, auctionEnd, isMinBid, simple = false }) => {
  const t = useTranslations('market');
  const { tezos } = useSelector((state: RootState) => state.global);
  const { general } = useSelector((state: RootState) => state.settings);
  const { locale } = useRouter();

  const renderer = (props: CountdownRenderProps) => {
    const { formatted, completed } = props;
    const { days, hours, minutes, seconds } = formatted;

    if (completed) {
      // TODO: decide what to do when bidding finish
      return <Text css={{ color: '$secondaryFill' }}>{t('auction-end')}</Text>;
    }

    return (
      <Box css={{ display: 'flex', '& > *': { marginRight: 8 } }}>
        <Box css={{ display: 'flex', flexDirection: 'column', width: 50 }}>
          <Box horizontal css={{ fontSize: 24 }}>
            {days}
          </Box>
          <Box horizontal css={{ color: '$secondaryFill', fontSize: 12 }}>
            {t('days')}
          </Box>
        </Box>

        <Box css={{ display: 'flex', flexDirection: 'column', width: 50 }}>
          <Box horizontal css={{ fontSize: 24 }}>
            {hours}
          </Box>
          <Box horizontal css={{ color: '$secondaryFill', fontSize: 12 }}>
            {t('hours')}
          </Box>
        </Box>
        <Box css={{ display: 'flex', flexDirection: 'column', width: 50 }}>
          <Box horizontal css={{ fontSize: 24 }}>
            {minutes}
          </Box>
          <Box horizontal css={{ color: '$secondaryFill', fontSize: 12 }}>
            {t('minutes')}
          </Box>
        </Box>
        <Box css={{ display: 'flex', flexDirection: 'column', width: 50 }}>
          <Box horizontal css={{ fontSize: 24 }}>
            {seconds}
          </Box>
          <Box horizontal css={{ color: '$secondaryFill', fontSize: 12 }}>
            {t('seconds')}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Styles.Container>
      {!simple && (
        <Box css={{ display: 'flex', flexDirection: 'column' }}>
          {isMinBid ? (
            <>
              <Text css={{ color: '$secondaryFill', marginBottom: '$space$5', fontSize: 12 }}>
                {t('no-bids')}&nbsp;
                <Tooltip description="No bids have been placed on this auction">
                  <i>help</i>
                </Tooltip>
              </Text>
              <Text css={{ color: '$primaryFill', marginBottom: '$space$5', fontSize: 12 }}>
                Reserve {currentBid}
                <Tezos />
              </Text>
            </>
          ) : (
            <>
              <Text css={{ color: '$secondaryFill', marginBottom: '$space$5', fontSize: 12 }}>
                Current bid&nbsp;
                <Tooltip
                  description={`Fiat value is for reference purposes only. The live Tezos price is currently ${getFiat(
                    general.currency,
                    tezos,
                    locale
                  )}, and is updated every five minutes.`}
                >
                  <i>help</i>
                </Tooltip>
              </Text>
              <Box css={{ fontSize: 24 }}>
                {currentBid}
                <Tezos />
              </Box>
              <Text css={{ color: '$secondaryFill', margin: '$space$5 0', fontSize: 12 }}>
                {getFiat(general.currency, currentBid * tezos, locale)}
              </Text>
            </>
          )}
        </Box>
      )}

      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        {!simple && (
          <Text css={{ color: '$secondaryFill', marginBottom: '$space$5', fontSize: 12 }}>{t('auction-ending')}</Text>
        )}
        <Countdown
          date={auctionEnd}
          renderer={renderer}
          onComplete={() => {
            // TODO: bidding ended. Do something
          }}
        />
      </Box>
    </Styles.Container>
  );
};
